/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import {
  accessAPI,
  formatFecha,
  tituloBase,
  Sidebar,
  Titulo,
  Boton,
  Formulario,
  TablaFiltrable
} from "../../shared";
import { estiloAccordion } from "./estilos";

export default function AuditoriasSG() {
  const [auditorias, setAuditorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [sidebarAuditoria, setSidebarAuditoria] = useState(false);
  const [formLoaderAuditoria, setFormLoaderAuditoria] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Auditorías SG";
    accessAPI(
      "GET",
      "admin/auditoriasg",
      null,
      (respuesta) => {
        setAuditorias(respuesta);
        setCargando(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar auditorías",
          sticky: true,
        });
        setCargando(false);
      },
    );
    return () => (document.title = tituloBase());
  }, []);

  function crearAuditoria(camposFormulario) {
    setFormLoaderAuditoria(true);
    accessAPI(
      "POST",
      "admin/auditoriasg",
      camposFormulario,
      (respuesta) => {
        navigate(`/auditoriasg/${respuesta.id}`);
      },
      (respuesta) => {
        setFormLoaderAuditoria(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear auditoría",
          sticky: true,
        });
      },
    );
  }

  const hoy = new Date();
  const finInclusivo = (a) =>
    new Date(new Date(a.fechaFin).getTime() + 24 * 60 * 60 * 1000);
  const auditoriasVigentes = auditorias.filter((a) => hoy < finInclusivo(a));
  const auditoriasVencidas = auditorias.filter((a) => hoy >= finInclusivo(a));

  const auditoriaFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true,
          },
          {
            nombre: "fechaInicio",
            tipoitemformularioid: 7,
            etiqueta:
              "Fecha de inicio (si queda en blanco se tomará la fecha de hoy)",
          },
          {
            nombre: "fechaFin",
            tipoitemformularioid: 7,
            etiqueta: "Fecha de fin",
            requerido: true,
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: "Crear auditoría",
        callback: crearAuditoria,
        validar: true,
      },
    ],
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        botones={
          <Boton callback={() => setSidebarAuditoria(true)}>
            Nueva auditoría
          </Boton>
        }
      >
        Auditorías SG
      </Titulo>

      {cargando ? (
        <Skeleton width="100%" height="10rem" borderRadius="6px" />
      ) : (
        <Card>
          <TablaFiltrable conFiltros={false}
            value={auditoriasVigentes}
            size="small"
            emptyMessage="No hay auditorías vigentes"
            selectionMode="single"
            onRowSelect={(e) => navigate(`/auditoriasg/${e.data.id}`)}
            rowHover
            nombreArchivo="auditorias_sg_vigentes"
          >
            <Column field="nombre" header="Nombre" />
            <Column
              header="Fecha Inicio"
              body={(rowData) => formatFecha(rowData.fechaInicio) || "—"}
            />
            <Column
              header="Fecha Fin"
              body={(rowData) => formatFecha(rowData.fechaFin) || "—"}
            />
            <Column
              header="Estado"
              body={(rowData) => (
                <Tag
                  severity={rowData.activa ? "success" : "danger"}
                  css={{ fontSize: "0.75rem" }}
                >
                  {rowData.activa ? "Activa" : "Desactivada"}
                </Tag>
              )}
              style={{ width: "8rem" }}
            />
          </TablaFiltrable>

          {auditoriasVencidas.length > 0 && (
            <Accordion
              transitionOptions={{ timeout: 0 }}
              css={{ marginTop: "1em", ...estiloAccordion }}
            >
              <AccordionTab header="Auditorías vencidas">
                <TablaFiltrable conFiltros={false}
                  value={auditoriasVencidas}
                  size="small"
                  selectionMode="single"
                  onRowSelect={(e) => navigate(`/auditoriasg/${e.data.id}`)}
                  rowHover
                  nombreArchivo="auditorias_sg_vencidas"
                >
                  <Column field="nombre" header="Nombre" />
                  <Column
                    header="Fecha Inicio"
                    body={(rowData) => formatFecha(rowData.fechaInicio) || "—"}
                  />
                  <Column
                    header="Fecha Fin"
                    body={(rowData) => formatFecha(rowData.fechaFin) || "—"}
                  />
                </TablaFiltrable>
              </AccordionTab>
            </Accordion>
          )}
        </Card>
      )}

      <Sidebar
        visible={sidebarAuditoria}
        onHide={() => setSidebarAuditoria(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Nueva auditoría</Titulo>
          <Formulario
            config={auditoriaFormConfig}
            loader={formLoaderAuditoria}
          />
        </div>
      </Sidebar>
    </>
  );
}
